import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { setActiveSpaceId } from 'spaces/store/actions';
import { getActiveSpace } from 'spaces/store/selectors';
import {
  getAuthUserId,
  getIsAdmin,
} from 'auth/store/auth/selectors';
import {
  confirmUserAudit,
} from 'auth/store/audit/actions';
import HeaderMessage from 'base/components/HeaderMessage';
import DataTable from 'base/components/table/DataTable/DataTable';
import MainTitle from 'base/components/MainTitle';
import Header from 'base/components/Header';
import Button from 'base/components/Button';
import { SmallLabelListCell } from 'base/components/table';
import api from 'api';
import { normalize } from 'normalizr';
import { usersSchema } from 'store/schema';
import { useMatch } from "react-router";
import {
  ContactInfoColumn,
  DivisionColumn,
  FirstNameColumn,
  LastNameColumn,
  PositionColumn,
  ProfilePictureColumn,
  SalutationColumn,
} from '../ContactTableComponents';
import { getContactsBySpaceId } from '../../store/selectors';
import { fetchAllContactsBySpaceId } from '../../store/actions';


function UserReview({ space }) {
    const dispatch = useDispatch();
    const isPastAuditDeadline = new Date(space.auditDeadline) < new Date();
    const beforeDeadlineText = (
        <>
            <strong>
                Corresponding users/roles have been blocked from this space.
                {' '}
                <br />
            </strong>
            Please review the users and assigned roles below to allow them
            to access this space again.
        </>
    );
    const afterDeadlineText = (
        <>
            Please review the users and assigned roles below until&nbsp;
            {dayjs(space.auditDeadline).format('DD.MM.YYYY')}
            &nbsp;, or they will be blocked from accessing this space.
        </>
    );

    return (
        <HeaderMessage
            type="warning"
            title={isPastAuditDeadline ? 'User review deadline expired' : 'User review required'}
            message={(
                <div>
                    <p>
                        {isPastAuditDeadline ? afterDeadlineText : beforeDeadlineText}
                        <br />
                        If you find any discrepancies, please contact an administrator.
                        Otherwise, click on the button below to confirm that everything is correct.
                    </p>
                    <Button variant="white" onClick={() => dispatch(confirmUserAudit(space.id))}>
                        All users and assigned roles are correct
                    </Button>
                </div>
            )}
        />
    );
}

function SpaceMembersPage() {
  const dispatch = useDispatch();
  const space = useSelector(getActiveSpace);
  const match = useMatch('/spaces/:spaceId/members');
  const spaceId = match?.params.spaceId;
  const contacts = useSelector((state) => getContactsBySpaceId(state, spaceId));
  const [usersById, setUsersById] = useState([]);
  const [rolesById, setRolesById] = useState([]);
  const isAdmin = useSelector(getIsAdmin);
  const myUserId = useSelector(getAuthUserId);

  const isSpaceOwner = space && space.owner && space.owner === myUserId;
  const hasAdminAccess = isAdmin || isSpaceOwner;

  useEffect(() => {
    dispatch(fetchAllContactsBySpaceId(spaceId));
    dispatch(setActiveSpaceId(spaceId));
    if (hasAdminAccess) {
      api.users.fetchAllBySpaceId(spaceId).toPromise()
        .then((response) => {
          const { entities } = normalize(response.body, usersSchema);
          setRolesById(entities.roles);
          setUsersById(entities.users);
        });
    }
  }, [dispatch, hasAdminAccess, spaceId]);

  const userIdsWithContacts = contacts.map((contact) => contact.user);
  const usersWithoutContacts = Object.keys(usersById)
    .filter((key) => !userIdsWithContacts.includes(parseInt(key, 10)))
    .reduce((agg, key) => ([
      ...agg,
      {
        ...usersById[key],
        user: parseInt(key, 10),
      },
    ]), []);
  const data = [
    ...contacts,
    ...usersWithoutContacts,
  ];

  const columns = [
    ProfilePictureColumn,
    SalutationColumn,
    LastNameColumn,
    FirstNameColumn,
    ContactInfoColumn,
    PositionColumn,
    DivisionColumn,
    {
      Header: 'Roles',
      id: 'roles',
      accessor: (row) => {
        if (row.user && usersById[row.user]) {
          const user = usersById[row.user];
          if (user.roles) {
            return user.roles.map((id) => rolesById[id].name);
          }
        }
        return [];
      },
      Cell: SmallLabelListCell('roles'),
      showOnMobile: true,
    },
  ];

  return (
    <>
      <Header>
        <MainTitle>
          {space && space.name}
          {' '}
          members
        </MainTitle>
      </Header>
      {(space && space.needsUserAudit && space.auditDeadline && hasAdminAccess)
        && <UserReview space={space} />}

      <DataTable
        data={data}
        columns={columns}
      />
    </>
  );
}



export default SpaceMembersPage;
